import { useParams, Link } from 'react-router-dom';
import { useOrder, type OrderLineItem } from '../hooks/useOrder';
import { LineItemTasks } from '../components/LineItemTasks';
import { StatusBadge } from '../components/StatusBadge';
import { safeFormatDate } from '../lib/utils';
import { ArrowLeft, Package, AlertCircle, AlertTriangle } from 'lucide-react';

/**
 * A line item is "at risk" when expected ship date is after promised ship date.
 */
function isLineItemAtRisk(item: OrderLineItem): boolean {
  if (!item.expectedShipDate || !item.promisedShipDate) return false;
  return item.expectedShipDate > item.promisedShipDate;
}

const schedulingStatusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-800',
  complete: 'bg-green-100 text-green-700',
  review_required: 'bg-orange-100 text-orange-700',
};

function LineItemCard({ item }: { item: OrderLineItem }) {
  const atRisk = isLineItemAtRisk(item);

  return (
    <div className={`bg-background rounded-lg border p-4 transition-colors ${
      atRisk ? 'border-danger/50' : 'border-border hover:border-primary/50'
    }`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
            atRisk ? 'bg-danger-subtle' : 'bg-background-muted'
          }`}>
            <Package className={`h-5 w-5 ${atRisk ? 'text-danger' : 'text-foreground-muted'}`} />
          </div>
          <div>
            <p className="font-medium text-foreground">{item.partNumber}</p>
            <p className="text-sm text-foreground-muted">Qty: {item.quantity}</p>
            {(item.expectedShipDate || item.expectedShipDateWorst) && (
              <div className="text-xs text-foreground-muted mt-0.5">
                {item.expectedShipDate && (
                  <span>
                    Expected: {safeFormatDate(item.expectedShipDate, 'MMM d, yyyy')}
                  </span>
                )}
                {item.expectedShipDateWorst && item.expectedShipDateWorst !== item.expectedShipDate && (
                  <span className="ml-2">
                    (worst: {safeFormatDate(item.expectedShipDateWorst, 'MMM d')})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {atRisk && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-danger-subtle text-danger-foreground">
              <AlertTriangle className="h-3 w-3" />
              At Risk
            </span>
          )}
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
              schedulingStatusColors[item.schedulingStatus] || 'bg-gray-100 text-gray-700'
            }`}
          >
            {item.schedulingStatus.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* At-risk explanation */}
      {atRisk && (
        <div className="flex items-start gap-2 mt-3 p-2.5 rounded-md bg-danger-subtle/50 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 text-danger mt-0.5 flex-shrink-0" />
          <span className="text-danger-foreground">
            Expected ship date ({safeFormatDate(item.expectedShipDate, 'MMM d')}) is after the promised date ({safeFormatDate(item.promisedShipDate, 'MMM d')}).
            This item may not ship on time.
          </span>
        </div>
      )}

      <LineItemTasks lineItemId={item.id} />
    </div>
  );
}

function OrderDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-6 bg-background-muted rounded w-32" />
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="h-8 bg-background-muted rounded w-48 mb-2" />
        <div className="h-5 bg-background-muted rounded w-36" />
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="h-12 bg-background-muted rounded" />
          <div className="h-12 bg-background-muted rounded" />
          <div className="h-12 bg-background-muted rounded" />
        </div>
      </div>
      <div className="h-6 bg-background-muted rounded w-40" />
      <div className="space-y-4">
        <div className="h-20 bg-background-muted rounded" />
        <div className="h-20 bg-background-muted rounded" />
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { data: order, isLoading, error } = useOrder(orderId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <OrderDetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">Failed to load order</h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <div className="rounded-lg border border-border bg-background p-8 text-center">
          <h3 className="text-lg font-medium text-foreground">Order not found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            The order you are looking for does not exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Get dates from first line item for the header metadata grid
  const firstItem = order.lineItems[0];
  const atRiskItems = order.lineItems.filter(isLineItemAtRisk);
  const hasAtRiskItems = atRiskItems.length > 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Orders
      </Link>

      {/* Order header card */}
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {order.gpSalesOrderNumber}
            </h1>
            <p className="text-foreground-muted mt-1">{order.customerName}</p>
          </div>
          <StatusBadge status={order.status} />
        </div>

        <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-border">
          <div>
            <p className="text-sm text-foreground-muted">Promised Ship Date</p>
            <p className="font-medium text-foreground mt-1">
              {safeFormatDate(order.promisedShipDate, 'MMM d, yyyy')}
            </p>
          </div>
          <div>
            <p className="text-sm text-foreground-muted">Expected Ship Date</p>
            <p className="font-medium text-foreground mt-1">
              {firstItem?.expectedShipDate
                ? safeFormatDate(firstItem.expectedShipDate, 'MMM d, yyyy')
                : order.expectedShipDate
                  ? safeFormatDate(order.expectedShipDate, 'MMM d, yyyy')
                  : 'Not scheduled'}
            </p>
          </div>
          <div>
            <p className="text-sm text-foreground-muted">Expected (Worst)</p>
            <p className="font-medium text-foreground mt-1">
              {safeFormatDate(firstItem?.expectedShipDateWorst, 'MMM d, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* At-risk banner */}
      {hasAtRiskItems && (
        <div className="flex items-center gap-3 rounded-lg border border-danger/30 bg-danger-subtle p-4">
          <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-danger-foreground">
              {atRiskItems.length} line {atRiskItems.length === 1 ? 'item' : 'items'} at risk
            </p>
            <p className="text-xs text-danger-foreground/80 mt-0.5">
              Expected ship date is after the promised date. These items may not ship on time.
            </p>
          </div>
        </div>
      )}

      {/* Line items section */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Line Items ({order.lineItems.length})
        </h2>
        {order.lineItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
            <Package className="h-8 w-8 text-foreground-muted mx-auto mb-3" />
            <p className="text-foreground-muted">No line items for this order.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {order.lineItems.map((item) => (
              <LineItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
